import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";

import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import LinearProgress from "@mui/material/LinearProgress";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Chip from "@mui/material/Chip";
const COLORS = {
  blue: "#1976D2",
  green: "#2E7D32",
  orange: "#ED6C02",
  purple: "#7B1FA2",
  red: "#D32F2F",
  teal: "#00897B",
  cyan: "#0097A7",
  indigo: "#3949AB",
  pink: "#D81B60",
  amber: "#F9A825",
  brown: "#795548",
  slate: "#546E7A",
};

const EMPTY_STATS = {
  examSchedules: 0,
  examDraft: 0,
  examApproved: 0,
  examPublished: 0,
  examClosed: 0,
  upcomingExams: 0,
  ongoingExams: 0,
  lockedExams: 0,
  valuationSchedules: 0,
  valuationApproved: 0,
  valuationCompleted: 0,
  normalValuations: 0,
  revaluationSchedules: 0,
  recountingSchedules: 0,
  totalScripts: 0,
  pendingScripts: 0,
  completedScripts: 0,
  verifiedScripts: 0,
};

function MetricCard({ title, value, subtitle, icon, color, progress }) {
  return (
    <Card
      sx={{
        height: "100%",
        borderRadius: "14px",
        overflow: "hidden",
        backgroundColor: `${color}12`,
        border: `1px solid ${color}30`,
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: `0 8px 24px ${color}25`,
        },
      }}
    >
      <MDBox p={2.2}>
        <MDBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
          <MDBox>
            <MDTypography variant="button" fontWeight="medium" color="text">
              {title}
            </MDTypography>

            <MDTypography variant="h3" fontWeight="bold" sx={{ mt: 1, color }}>
              {typeof value === "number" ? value.toLocaleString("en-IN") : value || 0}
            </MDTypography>

            <MDTypography
              variant="caption"
              color="black"
              sx={{ display: "block", mt: 0.5 }}
              fontSize="0.875rem"
            >
              {subtitle}
            </MDTypography>
          </MDBox>

          <MDBox
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{
              width: 48,
              height: 48,
              minWidth: 48,
              borderRadius: "12px",
              backgroundColor: `${color}20`,
              color,
            }}
          >
            <Icon fontSize="large">{icon}</Icon>
          </MDBox>
        </MDBox>

        {progress !== undefined && (
          <MDBox mt={2}>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, Math.max(0, progress))}
              sx={{
                height: 7,
                borderRadius: 5,
                backgroundColor: `${color}20`,
                "& .MuiLinearProgress-bar": {
                  backgroundColor: color,
                  borderRadius: 5,
                },
              }}
            />

            <MDTypography
              variant="caption"
              color="black"
              sx={{ display: "block", mt: 0.5 }}
              fontSize="0.875rem"
            >
              {progress.toFixed(1)}% of all scripts
            </MDTypography>
          </MDBox>
        )}
      </MDBox>
    </Card>
  );
}

MetricCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  subtitle: PropTypes.string,
  icon: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  progress: PropTypes.number,
};

function StatusRow({ label, value, color }) {
  return (
    <MDBox display="flex" justifyContent="space-between" alignItems="center" py={1} gap={1}>
      <MDBox display="flex" alignItems="center" gap={1}>
        <Box
          sx={{
            width: 9,
            height: 9,
            borderRadius: "50%",
            backgroundColor: color,
          }}
        />

        <MDTypography variant="button" fontWeight="regular" fontSize="1.175rem">
          {label}
        </MDTypography>
      </MDBox>

      <Chip
        label={Number(value || 0).toLocaleString("en-IN")}
        size="medium"
        sx={{
          fontWeight: "bold",
          fontSize: "1.175rem",
          height: 36,
          backgroundColor: color,
          color: "#FFFFFF",
          "& .MuiChip-label": {
            px: 1.5,
          },
        }}
      />
    </MDBox>
  );
}

StatusRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  color: PropTypes.string.isRequired,
};

export default function ExamDashboard({ onNavigate, onToggleView }) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [stats, setStats] = useState(EMPTY_STATS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchDashboard = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const token = sessionStorage.getItem("token");

      const storedUser = JSON.parse(sessionStorage.getItem("user") || "{}");

      const clientId = storedUser.client_id;

      const collegeId = storedUser.college_id || sessionStorage.getItem("college_id");

      if (!token || !clientId || !collegeId) {
        throw new Error("Login session, client ID, or college ID is missing.");
      }

      const response = await axios.get(
        `${API_BASE_URL}/api/exam-dashboard/${clientId}/${collegeId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const result = response.data?.data ?? response.data;

      setStats({
        ...EMPTY_STATS,
        ...result,
      });
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Unable to load exam dashboard.");
    } finally {
      setLoading(false);
    }
  }, [API_BASE_URL]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const totalScripts = Number(stats.totalScripts || 0);
  const completedScripts = Number(stats.completedScripts || 0);
  const verifiedScripts = Number(stats.verifiedScripts || 0);
  const pendingScripts = Number(stats.pendingScripts || 0);

  const evaluatedScripts = completedScripts + verifiedScripts;

  const evaluationProgress = totalScripts > 0 ? (evaluatedScripts / totalScripts) * 100 : 0;

  if (loading) {
    return (
      <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="220px">
        <CircularProgress color="info" />
      </MDBox>
    );
  }

  return (
    <MDBox p={2}>
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
        mb={3}
      >
        <MDBox>
          <MDTypography variant="h5" fontWeight="bold">
            EXAM DASHBOARD
          </MDTypography>

          <MDTypography variant="button" color="text">
            Examination scheduling and script valuation overview
          </MDTypography>
        </MDBox>

        <MDButton
          variant="outlined"
          color="info"
          onClick={onToggleView}
          startIcon={<Icon>view_list</Icon>}
        >
          Show Tabs
        </MDButton>
      </MDBox>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3 }}
          action={
            <MDButton color="error" size="small" onClick={fetchDashboard}>
              Retry
            </MDButton>
          }
        >
          {error}
        </Alert>
      )}

      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            title="Exam Schedules"
            value={stats.examSchedules}
            subtitle={`Draft: ${stats.examDraft} | Approved: ${stats.examApproved} | Published: ${stats.examPublished} | Closed: ${stats.examClosed}`}
            icon="event"
            color={COLORS.blue}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            title="Upcoming Exams"
            value={stats.upcomingExams}
            subtitle="Approved or published exams"
            icon="event-available"
            color={COLORS.green}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            title="Valuation Schedules"
            value={stats.valuationSchedules}
            subtitle="All valuation rounds"
            icon="assignment"
            color={COLORS.orange}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            title="Total Scripts"
            value={totalScripts}
            subtitle="Scripts in valuation details"
            icon="description"
            color={COLORS.teal}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            title="Pending Scripts"
            value={pendingScripts}
            subtitle="Awaiting evaluation"
            icon="pending-actions"
            color={COLORS.red}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            title="Completed"
            value={completedScripts}
            subtitle="Evaluation completed"
            icon="task-alt"
            color={COLORS.blue}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            title="Verified"
            value={verifiedScripts}
            subtitle="Scripts verified"
            icon="verified"
            color={COLORS.green}
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            title="Evaluation Progress"
            value={`${evaluationProgress.toFixed(1)}%`}
            subtitle={`${evaluatedScripts.toLocaleString("en-IN")} of ${totalScripts.toLocaleString(
              "en-IN"
            )} scripts evaluated`}
            icon="analytics"
            color={COLORS.purple}
            progress={evaluationProgress}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%", borderRadius: "14px" }}>
            <MDBox p={2.5}>
              <MDTypography variant="h5" fontWeight="bold">
                Exam Workflow
              </MDTypography>

              <MDTypography variant="button" color="text">
                Schedule distribution by status
              </MDTypography>

              <Divider sx={{ my: 1.5 }} />

              <StatusRow label="Draft" value={stats.examDraft} color="#78909C" />

              <StatusRow label="Approved" value={stats.examApproved} color={COLORS.blue} />

              <StatusRow label="Published" value={stats.examPublished} color={COLORS.green} />

              <StatusRow label="Closed" value={stats.examClosed} color={COLORS.purple} />

              <Divider sx={{ my: 1 }} />

              <StatusRow label="Ongoing Exams" value={stats.ongoingExams} color={COLORS.orange} />

              <StatusRow label="Locked Exams" value={stats.lockedExams} color={COLORS.red} />
            </MDBox>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: "100%", borderRadius: "14px" }}>
            <MDBox p={2.5}>
              <MDTypography variant="h6" fontWeight="bold">
                Valuation Overview
              </MDTypography>

              <MDTypography variant="button" color="text">
                Schedule types and workflow progress
              </MDTypography>

              <Divider sx={{ my: 1.5 }} />

              <StatusRow
                label="Normal Valuation"
                value={stats.normalValuations}
                color={COLORS.blue}
              />

              <StatusRow
                label="Revaluation"
                value={stats.revaluationSchedules}
                color={COLORS.orange}
              />

              <StatusRow
                label="Recounting"
                value={stats.recountingSchedules}
                color={COLORS.purple}
              />

              <StatusRow
                label="Approved Schedules"
                value={stats.valuationApproved}
                color={COLORS.teal}
              />

              <StatusRow
                label="Completed Schedules"
                value={stats.valuationCompleted}
                color={COLORS.green}
              />
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
}

ExamDashboard.propTypes = {
  onNavigate: PropTypes.func.isRequired,
  onToggleView: PropTypes.func.isRequired,
};
